import React, { useState } from "react";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import "../../styles/clinicsetup.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const InviteVetByEmailModal = ({ open, onClose, clinicId, userType = "VETERINARIAN" }) => {
  const [email, setEmail] = useState("");
  const getDefaultMessage = () => {
    if (userType === "CLINIC_MODERATOR") {
      return "We would like to invite you to join our clinic as a moderator. Your role will involve managing clinic operations and supporting our veterinary team.";
    }
    return "We would like to invite you to join our veterinary team. We look forward to working with you.";
  };
  
  const [message, setMessage] = useState(getDefaultMessage());
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!message.trim()) {
      newErrors.message = "Message is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!clinicId) {
      showErrorToast("No clinic ID found. Please complete clinic setup first.", {
        title: "Setup Required",
        duration: 5000
      });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem("access_token");
      
      // Use different endpoints based on user type
      const endpoint = userType === "CLINIC_MODERATOR" 
        ? `${API_BASE_URL}/api/v1/clinic-members/invite-moderator-by-email`
        : `${API_BASE_URL}/api/v1/clinic-members/invite-by-email`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          clinic_id: clinicId,
          email: email.trim(),
          message: message.trim(),
          user_type: userType
        })
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        showSuccessToast("Invitation sent successfully!", {
          title: "Invitation Sent",
          duration: 4000
        });
        
        // Reset form and close modal
        setEmail("");
        setMessage(getDefaultMessage());
        onClose();
      } else {
        const errorMessage = result.message || "Failed to send invitation";
        showErrorToast(errorMessage, {
          title: "Invitation Failed",
          duration: 5000
        });
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
      showErrorToast("Failed to send invitation. Please try again.", {
        title: "Error",
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setEmail("");
      setMessage(getDefaultMessage());
      setErrors({});
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "30px",
        width: "90%",
        maxWidth: "500px",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "25px"
        }}>
          <h2 style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: "600",
            color: "#1F2937",
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            Invite {userType === "CLINIC_MODERATOR" ? "Clinic Moderator" : "Veterinarian"}
          </h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: isLoading ? "not-allowed" : "pointer",
              color: "#6B7280",
              padding: "4px",
              borderRadius: "4px"
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {userType === "CLINIC_MODERATOR" ? "Moderator" : "Veterinarian"} Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="veterinarian@example.com"
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.email ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.email ? "#EF4444" : "#D1D5DB"}
            />
            {errors.email && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.email}
              </div>
            )}
          </div>

          <div style={{ marginBottom: "25px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              Invitation Message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your invitation message..."
              disabled={isLoading}
              rows={4}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.message ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                resize: "vertical",
                minHeight: "100px",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.message ? "#EF4444" : "#D1D5DB"}
            />
            {errors.message && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.message}
              </div>
            )}
          </div>

          <div style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end"
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#F3F4F6",
                color: "#374151",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontFamily: '"Red Hat Display", sans-serif',
                transition: "background-color 0.2s ease"
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = "#E5E7EB")}
              onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = "#F3F4F6")}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: isLoading ? "#9CA3AF" : "#3B82F6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontFamily: '"Red Hat Display", sans-serif',
                transition: "background-color 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
              onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = "#2563EB")}
              onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = "#3B82F6")}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }} />
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </button>
          </div>
        </form>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InviteVetByEmailModal;
